<template>
  <a-drawer :open="visible" :title="node ? `服务器 #${node.id} · ${node.name}` : '服务器详情'" width="640" @close="$emit('update:visible', false)">
    <a-spin :spinning="loading">
      <template v-if="detail">
        <a-card size="small" style="margin-bottom:16px;background:#fafafa">
          <a-row :gutter="16" align="middle">
            <a-col :span="8">
              <div style="color:#6b7280;font-size:12px">实时上行</div>
              <div :style="{ fontSize: '20px', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, color: upBps > 100 * 1024 ? '#f5222d' : '#374151' }">
                {{ formatRate(upBps) }}
              </div>
            </a-col>
            <a-col :span="8">
              <div style="color:#6b7280;font-size:12px">实时下行</div>
              <div :style="{ fontSize: '20px', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, color: bandwidthStatusColor }">
                {{ formatRate(downBps) }}
                <a-tag
                  v-if="bandwidthUsage"
                  :color="bandwidthUsage.tagColor"
                  style="margin-left:6px;font-size:11px;vertical-align:middle"
                >
                  {{ bandwidthUsage.percentLabel }}
                </a-tag>
              </div>
              <div
                v-if="bandwidthUsage"
                style="height:6px;background:#e5e7eb;border-radius:999px;margin-top:6px;overflow:hidden"
              >
                <div
                  :style="{
                    height: '100%',
                    width: bandwidthUsage.barWidth,
                    background: bandwidthUsage.barColor,
                    transition: 'width .2s ease',
                  }"
                />
              </div>
              <div v-if="bandwidthLimitBps > 0" style="color:#9ca3af;font-size:11px;margin-top:2px">
                上限 {{ formatRate(bandwidthLimitBps) }}
              </div>
            </a-col>
            <a-col :span="8">
              <div style="color:#6b7280;font-size:12px">活跃连接</div>
              <div :style="{ fontSize: '20px', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, color: detail.active_clients > 0 ? '#1677ff' : '#9ca3af' }">
                {{ detail.active_clients }}
              </div>
              <div style="color:#9ca3af;font-size:11px;margin-top:2px">
                累计 ↑{{ formatBytes(detail.bytes_up) }} ↓{{ formatBytes(detail.bytes_down) }}
              </div>
            </a-col>
          </a-row>
        </a-card>

        <a-descriptions :column="2" bordered size="small" style="margin-bottom:16px">
          <a-descriptions-item label="ID">{{ detail.id }}</a-descriptions-item>
          <a-descriptions-item label="名称">{{ detail.name }}</a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="statusColor">{{ statusLabel }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="公网 IP">
            <span v-if="detail.resolved_ip" style="font-family:ui-monospace,Menlo,monospace">{{ detail.resolved_ip }}</span>
            <span v-else style="color:#9ca3af">{{ detail.ip_public ? '解析中或失败' : '未设置' }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="端口占用">{{ detail.used_ports }}/{{ detail.capacity_ports }}</a-descriptions-item>
          <a-descriptions-item label="CPU">{{ detail.cpu_percent ? detail.cpu_percent + ' %' : '-' }}</a-descriptions-item>
          <a-descriptions-item label="内存">{{ detail.mem_percent ? detail.mem_percent + ' %' : '-' }}</a-descriptions-item>
          <a-descriptions-item label="磁盘">{{ detail.disk_percent ? detail.disk_percent + ' %' : '-' }}</a-descriptions-item>
          <a-descriptions-item label="最后心跳">
            {{ detail.last_heartbeat ? new Date(detail.last_heartbeat).toLocaleString('zh-CN') : '从未' }}
          </a-descriptions-item>
          <a-descriptions-item label="Agent 版本" :span="2">
            <a-tag v-if="detail.agent_version" color="purple">{{ detail.agent_version }}{{ detail.agent_git_sha ? ' · ' + detail.agent_git_sha : '' }}</a-tag>
            <span v-else style="color:#9ca3af">未上报</span>
          </a-descriptions-item>
          <a-descriptions-item label="升级时间" :span="2">
            <span v-if="detail.upgraded_at" style="font-family:ui-monospace,Menlo,monospace">
              {{ new Date(detail.upgraded_at).toLocaleString('zh-CN') }}
            </span>
            <span v-else style="color:#9ca3af">从未升级过</span>
          </a-descriptions-item>
          <a-descriptions-item label="到期时间" :span="2">
            <span v-if="detail.expire_at" :style="expireStyle">
              {{ new Date(detail.expire_at).toLocaleString('zh-CN') }}
            </span>
            <span v-else style="color:#9ca3af">永不过期</span>
            <a-button v-if="detail.expire_at" size="small" type="link" style="margin-left:8px" @click="clearExpire">清除</a-button>
          </a-descriptions-item>
          <a-descriptions-item label="总成本" :span="2">
            <span v-if="detail.total_cost_cents != null">
              ¥{{ (detail.total_cost_cents / 100).toFixed(2) }}
            </span>
            <span v-else style="color:#f5222d">未设置</span>
          </a-descriptions-item>
          <a-descriptions-item label="下行带宽" :span="2">
            <span v-if="bandwidthLimitBps > 0">
              {{ detail.bandwidth_down_mbps }} Mbps ({{ formatRate(bandwidthLimitBps) }})
            </span>
            <span v-else style="color:#9ca3af">未设置</span>
          </a-descriptions-item>
          <a-descriptions-item label="预置端口" :span="2">
            <a-space v-if="detail.preset_ports?.length" :size="4" wrap>
              <a-tag v-for="port in detail.preset_ports" :key="port">{{ port }}</a-tag>
            </a-space>
            <span v-else style="color:#9ca3af">未设置</span>
          </a-descriptions-item>
        </a-descriptions>

        <a-alert
          v-if="detail.status !== 'online'"
          type="warning"
          show-icon
          message="服务器不在线 — 重启/停止/升级 操作不可用"
          style="margin-bottom:16px"
        />

        <a-space>
          <a-button type="primary" @click="openEdit">编辑</a-button>
          <a-button
            :disabled="detail.status !== 'online'"
            :loading="busy === 'restart'"
            @click="askAction('restart')"
          >重启</a-button>
          <a-button
            danger
            :disabled="detail.status !== 'online'"
            :loading="busy === 'stop'"
            @click="askAction('stop')"
          >停止</a-button>
          <a-button
            :disabled="detail.status !== 'online'"
            :loading="busy === 'upgrade'"
            @click="askAction('upgrade')"
          >升级</a-button>
          <a-button
            danger
            :loading="busy === 'delete'"
            @click="confirmDelete"
          >删除服务器</a-button>
        </a-space>

        <a-divider />
        <a-typography-title :level="5" style="margin-top:0">
          此服务器上的 Proxy（{{ nodeProxies.length }}）
        </a-typography-title>
        <a-table
          :data-source="nodeProxies"
          :columns="proxyColumns"
          :loading="proxiesLoading"
          :pagination="false"
          row-key="proxy_id"
          size="small"
          :scroll="{ x: 560 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'port'">
              <span style="font-family:ui-monospace,Menlo,monospace">{{ record.port }}</span>
            </template>
            <template v-else-if="column.key === 'regions'">
              {{ (record.region_names?.length ? record.region_names : [record.region_name]).join('、') }}
            </template>
            <template v-else-if="column.key === 'user'">
              {{ record.proxy_user_wechat_nickname || record.username }}
            </template>
            <template v-else-if="column.key === 'status'">
              <a-tag :color="record.status === 'active' ? 'green' : 'blue'">
                {{ record.status === 'active' ? '运行中' : '创建中' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'clients'">
              {{ record.active_clients }}/{{ record.max_concurrent_clients }}
            </template>
            <template v-else-if="column.key === 'down'">
              {{ formatRate(record.down_bps || 0) }}
            </template>
            <template v-else-if="column.key === 'expire'">
              {{ record.expire_at ? new Date(record.expire_at).toLocaleDateString('zh-CN') : '—' }}
            </template>
          </template>
        </a-table>

        <a-divider />
        <a-typography-title :level="5" style="margin-top:0">服务器运维连接信息</a-typography-title>
        <a-form layout="vertical">
          <a-row :gutter="12">
            <a-col :span="12">
              <a-form-item label="SSH Host">
                <a-input v-model:value="opsForm.ssh_host" placeholder="例如 103.40.14.174" />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="SSH Port">
                <a-input-number v-model:value="opsForm.ssh_port" :min="1" :max="65535" style="width:100%" />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="认证方式">
                <a-select v-model:value="opsForm.ssh_auth_type" :options="authTypeOptions" />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="12">
            <a-col :span="12">
              <a-form-item label="SSH 用户名">
                <a-input v-model:value="opsForm.ssh_username" placeholder="root" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="负责人">
                <a-input v-model:value="opsForm.owner" placeholder="可选" />
              </a-form-item>
            </a-col>
          </a-row>
          <a-space>
            <a-button type="primary" :loading="opsSaving" @click="saveOpsProfile">保存连接信息</a-button>
            <a-tag :color="sshStatusColor">
              SSH 检查：{{ sshStatusText }}
            </a-tag>
            <span v-if="opsProfile?.last_ssh_check_error" style="color:#ef4444">{{ opsProfile.last_ssh_check_error }}</span>
          </a-space>
        </a-form>

        <a-divider />
        <a-typography-title :level="5" style="margin-top:0">凭据</a-typography-title>
        <a-form layout="vertical">
          <a-form-item v-if="opsForm.ssh_auth_type === 'private_key'" label="私钥">
            <a-textarea
              v-model:value="credForm.private_key"
              :rows="4"
              placeholder="粘贴 OpenSSH 私钥"
            />
          </a-form-item>
          <a-form-item v-else label="密码">
            <a-input-password v-model:value="credForm.password" placeholder="输入密码" />
          </a-form-item>
          <a-space>
            <a-button type="primary" :loading="credSaving" @click="saveCredential">保存凭据</a-button>
            <a-button :loading="credVerifying" @click="verifyCredential">验证连接</a-button>
            <span v-if="opsProfile?.has_credential" style="color:#16a34a">已保存凭据</span>
            <span v-else style="color:#9ca3af">未保存凭据</span>
          </a-space>
        </a-form>

        <a-divider />
        <a-typography-title :level="5" style="margin-top:0">服务器动作（SSH/Agent）</a-typography-title>
        <a-space wrap>
          <a-button :loading="nodeActionBusy === 'restart_agent'" @click="runNodeOpsAction('restart_agent')">重启 Agent</a-button>
          <a-button :loading="nodeActionBusy === 'stop_agent'" danger @click="runNodeOpsAction('stop_agent')">停止 Agent</a-button>
          <a-button :loading="nodeActionBusy === 'start_agent'" @click="runNodeOpsAction('start_agent')">启动 Agent</a-button>
          <a-button :loading="nodeActionBusy === 'upgrade_agent'" @click="runNodeOpsAction('upgrade_agent')">升级 Agent</a-button>
          <a-button :loading="nodeActionBusy === 'restart_node'" danger @click="runNodeOpsAction('restart_node')">重启服务器</a-button>
          <a-button :loading="nodeActionBusy === 'run_diag'" @click="runNodeOpsAction('run_diag')">运行诊断</a-button>
        </a-space>

        <a-divider />
        <a-typography-title :level="5" style="margin-top:0">最近操作记录</a-typography-title>
        <a-list
          size="small"
          bordered
          :data-source="opLogs"
          :locale="{ emptyText: '暂无操作记录' }"
        >
          <template #renderItem="{ item }">
            <a-list-item>
              <div style="width:100%">
                <div style="display:flex;justify-content:space-between">
                  <span>{{ item.op_type }} · {{ item.status }}</span>
                  <span style="color:#9ca3af">{{ item.requested_at }}</span>
                </div>
                <div v-if="item.error_msg" style="color:#ef4444">{{ item.error_msg }}</div>
              </div>
            </a-list-item>
          </template>
        </a-list>
      </template>
    </a-spin>
    <NodeEditDrawer v-model:visible="editVisible" :node="detail" @saved="onEditSaved" />
  </a-drawer>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { message } from 'ant-design-vue';
import {
  getNode,
  controlNode,
  deleteNode,
  updateNode,
  getNodeOpsProfile,
  updateNodeOpsProfile,
  saveNodeCredential,
  verifyNodeCredential,
  runNodeAction,
  listNodeOperationLogs,
} from '../api/nodes';
import {
  confirmNodeControl,
  confirmOperation,
  NODE_DELETE_HINT,
  type NodeControlAction,
} from '../utils/operationHints';
import NodeEditDrawer from './NodeEditDrawer.vue';
import { listProxies } from '../api/proxies';
import type { NodeItem, ProxyItem, NodeOpsProfile, NodeOperationLog } from '../types';
import { formatTrafficBytes, formatTrafficRate } from '../utils/trafficFormat';
import { confirmForceRun, runGuarded } from '../utils/maintenanceForce';

const props = withDefaults(
  defineProps<{ visible: boolean; node: NodeItem | null; trafficRate?: { upBps: number; downBps: number } }>(),
  { trafficRate: () => ({ upBps: 0, downBps: 0 }) }
);
const emit = defineEmits<{ (e: 'update:visible', v: boolean): void; (e: 'refresh'): void }>();

// Snapshot the latest traffic rate so the drawer doesn't show stale
// numbers while it's open. Parent pushes the same prop on each refresh.
const upBps = computed(() => props.trafficRate.upBps);
const downBps = computed(() => props.trafficRate.downBps);

// Display helpers. Speeds <1 KB/s show as KB/s instead of MB/s so a
// single player on a slow line doesn't read "0.00 MB/s". Cap shown
// precision at 2 decimals.
const formatRate = formatTrafficRate;
const formatBytes = formatTrafficBytes;

const bandwidthLimitBps = computed(() => {
  const mbps = Number(detail.value?.bandwidth_down_mbps || 0);
  if (!Number.isFinite(mbps) || mbps <= 0) return 0;
  return (mbps * 1024 * 1024) / 8;
});

const bandwidthUsage = computed(() => {
  const limit = bandwidthLimitBps.value;
  if (limit <= 0) return null;
  const ratio = downBps.value / limit;
  const percent = Math.max(0, ratio * 100);
  if (percent >= 90) {
    return {
      percent,
      percentLabel: `${percent.toFixed(0)}%`,
      tagColor: 'red',
      barColor: '#ef4444',
      textColor: '#dc2626',
      barWidth: `${Math.min(100, percent).toFixed(0)}%`,
    };
  }
  if (percent >= 70) {
    return {
      percent,
      percentLabel: `${percent.toFixed(0)}%`,
      tagColor: 'gold',
      barColor: '#f59e0b',
      textColor: '#ca8a04',
      barWidth: `${Math.min(100, percent).toFixed(0)}%`,
    };
  }
  return {
    percent,
    percentLabel: `${percent.toFixed(0)}%`,
    tagColor: 'green',
    barColor: '#22c55e',
    textColor: '#16a34a',
    barWidth: `${Math.min(100, percent).toFixed(0)}%`,
  };
});
const bandwidthStatusColor = computed(() => bandwidthUsage.value?.textColor || '#374151');

const detail = ref<NodeItem | null>(null);
const loading = ref(false);
const busy = ref<string | null>(null);
const editVisible = ref(false);
const opsProfile = ref<NodeOpsProfile | null>(null);
const opLogs = ref<NodeOperationLog[]>([]);
const opsSaving = ref(false);
const credSaving = ref(false);
const credVerifying = ref(false);
const nodeActionBusy = ref<string | null>(null);
const authTypeOptions = [
  { value: 'password', label: '密码' },
  { value: 'private_key', label: '私钥' },
];
const opsForm = ref({
  ssh_host: '',
  ssh_port: 22,
  ssh_username: '',
  ssh_auth_type: 'password' as 'password' | 'private_key',
  owner: '',
});
const credForm = ref({
  password: '',
  private_key: '',
});

const statusColor = computed(() => {
  switch (detail.value?.status) {
    case 'online': return 'green';
    case 'offline': return 'default';
    case 'new': return 'blue';
    default: return 'default';
  }
});

const statusLabel = computed(() => {
  switch (detail.value?.status) {
    case 'online': return '在线';
    case 'offline': return '离线';
    case 'new': return '未加入';
    default: return detail.value?.status || '';
  }
});

// Red + bold when expire_at is in the past. Otherwise neutral.
const expireStyle = computed(() => {
  const exp = detail.value?.expire_at;
  if (!exp) return {};
  const t = new Date(exp).getTime();
  if (isNaN(t) || t >= Date.now()) return {};
  return { color: '#dc2626', fontWeight: 600 as const };
});

const clearExpire = async () => {
  if (!detail.value) return;
  try {
    await updateNode(detail.value.id, { expire_at: null });
    message.success('已清除到期时间');
    emit('refresh');
    await load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '清除失败');
  }
};

const openEdit = () => { editVisible.value = true; };

const onEditSaved = async () => {
  emit('refresh');
  await load();
};

const load = async () => {
  if (!props.node) return;
  loading.value = true;
  try {
    const r = await getNode(props.node.id);
    detail.value = r.data;
    const [profileResp, logResp] = await Promise.allSettled([
      getNodeOpsProfile(props.node.id),
      listNodeOperationLogs(props.node.id, 20),
    ]);
    if (profileResp.status === 'fulfilled') {
      opsProfile.value = profileResp.value.data;
    } else {
      opsProfile.value = null;
    }
    if (logResp.status === 'fulfilled') {
      opLogs.value = logResp.value.data || [];
    } else {
      opLogs.value = [];
    }
    opsForm.value = {
      ssh_host: opsProfile.value?.ssh_host || '',
      ssh_port: opsProfile.value?.ssh_port || 22,
      ssh_username: opsProfile.value?.ssh_username || '',
      ssh_auth_type: opsProfile.value?.ssh_auth_type || 'password',
      owner: opsProfile.value?.owner || '',
    };
    credForm.value.password = '';
    credForm.value.private_key = '';
  } catch (e: any) {
    message.error('加载失败: ' + (e.response?.data?.error?.message || e.message));
  } finally {
    loading.value = false;
  }
};

const nodeProxies = ref<ProxyItem[]>([]);
const proxiesLoading = ref(false);
const proxyColumns = [
  { title: '端口', key: 'port', width: 80 },
  { title: '大区', key: 'regions', ellipsis: true },
  { title: '用户', key: 'user', ellipsis: true },
  { title: '状态', key: 'status', width: 80 },
  { title: '连接', key: 'clients', width: 70 },
  { title: '下行', key: 'down', width: 100 },
  { title: '到期', key: 'expire', width: 100 },
];

const loadNodeProxies = async () => {
  if (!props.node) return;
  proxiesLoading.value = true;
  try {
    const r = await listProxies({ node_id: props.node.id });
    const rows: ProxyItem[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? []);
    nodeProxies.value = rows
      .filter((p) => p.node_id === props.node?.id && (p.status === 'active' || p.status === 'creating'))
      .sort((a, b) => a.port - b.port);
  } catch (e: any) {
    nodeProxies.value = [];
    message.error(`加载 Proxy 列表失败：${e?.response?.data?.message || e?.message || e}`);
  } finally {
    proxiesLoading.value = false;
  }
};

watch(() => [props.visible, props.node?.id], ([v]) => {
  if (v && props.node) {
    load();
    loadNodeProxies();
  }
});

const askAction = (action: NodeControlAction) => {
  if (!detail.value || detail.value.status !== 'online') {
    message.error('服务器不在线，无法执行控制命令');
    return;
  }
  confirmNodeControl(action, () => onAction(action));
};

const onAction = async (action: NodeControlAction) => {
  if (!detail.value) return;
  busy.value = action;
  try {
    const nodeId = detail.value.id;
    const outcome = await runGuarded((force) => controlNode(nodeId, action, force), confirmForceRun);
    if (!outcome) return;
    const r = outcome.result;
    message.success(`${action} 已派发${r.data?.acked ? ' (已 ack)' : ' (等待重连)'}`);
    emit('refresh');
    // After a stop/restart/upgrade the drawer is showing stale data;
    // close it once the action has gone through.
    emit('update:visible', false);
  } catch (e: any) {
    message.error(`${action} 失败: ` + (e.response?.data?.error?.message || e.message));
  } finally {
    busy.value = null;
  }
};

const confirmDelete = () => {
  confirmOperation(NODE_DELETE_HINT, onDelete);
};

const onDelete = async () => {
  if (!detail.value) return;
  busy.value = 'delete';
  try {
    await deleteNode(detail.value.id);
    message.success('服务器已删除');
    emit('refresh');
    emit('update:visible', false);
  } catch (e: any) {
    const code = e.response?.data?.error?.code;
    if (code === 'NODE_HAS_ACTIVE_PROXIES') {
      message.error('该服务器还有 active / creating 节点，请先删除节点');
    } else {
      message.error(e.response?.data?.error?.message || '删除服务器失败');
    }
  } finally {
    busy.value = null;
  }
};

const saveOpsProfile = async () => {
  if (!props.node) return;
  opsSaving.value = true;
  try {
    await updateNodeOpsProfile(props.node.id, {
      ssh_host: opsForm.value.ssh_host || null,
      ssh_port: opsForm.value.ssh_port,
      ssh_username: opsForm.value.ssh_username || null,
      ssh_auth_type: opsForm.value.ssh_auth_type,
      owner: opsForm.value.owner || null,
    });
    message.success('连接信息已保存');
    await load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '保存失败');
  } finally {
    opsSaving.value = false;
  }
};

const saveCredential = async () => {
  if (!props.node) return;
  credSaving.value = true;
  try {
    if (opsForm.value.ssh_auth_type === 'private_key') {
      await saveNodeCredential(props.node.id, {
        auth_type: 'private_key',
        private_key: credForm.value.private_key,
      });
    } else {
      await saveNodeCredential(props.node.id, {
        auth_type: 'password',
        password: credForm.value.password,
      });
    }
    message.success('凭据已保存');
    await load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '保存凭据失败');
  } finally {
    credSaving.value = false;
  }
};

const verifyCredential = async () => {
  if (!props.node) return;
  credVerifying.value = true;
  try {
    await verifyNodeCredential(props.node.id);
    message.success('SSH 验证通过');
    await load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || 'SSH 验证失败');
    await load();
  } finally {
    credVerifying.value = false;
  }
};

const runNodeOpsAction = async (action: 'restart_agent' | 'stop_agent' | 'start_agent' | 'upgrade_agent' | 'restart_node' | 'run_diag') => {
  if (!props.node) return;
  nodeActionBusy.value = action;
  try {
    const nodeId = props.node.id;
    const outcome = await runGuarded((force) => runNodeAction(nodeId, force ? { action, force: true } : { action }), confirmForceRun);
    if (!outcome) return;
    message.success(`操作已执行：${action}`);
    await load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || `操作失败：${action}`);
    await load();
  } finally {
    nodeActionBusy.value = null;
  }
};

const sshStatusColor = computed(() => {
  const st = opsProfile.value?.last_ssh_check_status;
  if (st === 'ok') return 'green';
  if (st === 'fail') return 'red';
  return 'default';
});

const sshStatusText = computed(() => {
  const st = opsProfile.value?.last_ssh_check_status;
  if (st === 'ok') return '通过';
  if (st === 'fail') return '失败';
  return '未知';
});
</script>
